import { useState } from 'react';
import { formatDateTime } from '../format.js';
import {
  FieldHardwareSchema,
  hardwarePathLabel,
  NUMERIC_HARDWARE_FIELDS,
  type HardwareConflict,
} from '@cer-autofill/shared';

export function HardwareConflictsPanel({
  conflicts,
  disabled,
  onResolve,
  fieldSubmittedAt,
}: {
  conflicts: readonly HardwareConflict[];
  disabled: boolean;
  fieldSubmittedAt?: string;
  onResolve: (id: string, choice: 'dashboard' | 'field' | 'edited', value?: unknown) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const unresolved = conflicts.filter((c) => !c.resolution);
  const resolved = conflicts.filter((c) => c.resolution);
  if (!conflicts.length) return null;
  return (
    <section
      id="hardware-conflicts"
      className="card"
      style={{ border: '2px solid #d69e2e', background: '#fffbeb' }}
    >
      <h3>
        {unresolved.length} hardware conflict(s){unresolved.length ? ' — approval blocked' : ''}
      </h3>
      <p>Review the dashboard and field values before approving for portal.</p>
      {unresolved.map((conflict) => (
        <div
          key={conflict.id}
          style={{ padding: 16, marginBlock: 16, border: '1px solid #d69e2e', borderRadius: 8 }}
        >
          <button
            type="button"
            onClick={() =>
              document.getElementById('report-hardware')?.scrollIntoView({ behavior: 'smooth' })
            }
          >
            {hardwarePathLabel(conflict.path)}
          </button>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16,
              marginBlock: 12,
            }}
          >
            <div>
              <strong>Dashboard</strong>
              <small style={{ display: 'block' }}>
                Entered {conflict.dashboardEntered?.by ?? 'unknown reviewer'}{' '}
                {conflict.dashboardEntered?.at
                  ? formatDateTime(conflict.dashboardEntered.at)
                  : '(legacy date unavailable)'}
              </small>
              <p>{display(conflict.dashboardValue)}</p>
            </div>
            <div>
              <strong>Field</strong>
              <small style={{ display: 'block' }}>
                Submitted {formatDateTime(fieldSubmittedAt ?? conflict.detectedAt)}
              </small>
              <p>{display(conflict.fieldValue)}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onResolve(conflict.id, 'dashboard')}
            >
              Keep dashboard
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onResolve(conflict.id, 'field')}
            >
              Use field value
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setEditing(conflict.id);
                setError('');
                setText(
                  Array.isArray(conflict.dashboardValue)
                    ? conflict.dashboardValue.join('\n')
                    : display(conflict.dashboardValue),
                );
              }}
            >
              Edit value
            </button>
          </div>
          {editing === conflict.id && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const parsed = parseConflictEdit(conflict, text);
                if ('error' in parsed) {
                  setError(parsed.error);
                  return;
                }
                const checked = validateConflictValue(conflict, parsed.value);
                if (checked) {
                  setError(checked);
                  return;
                }
                setError('');
                const value = parsed.value;
                onResolve(conflict.id, 'edited', value);
              }}
            >
              <label>
                Replacement value {Array.isArray(conflict.dashboardValue) && '(one entry per line)'}
                {typeof conflict.dashboardValue === 'boolean' ? (
                  <select value={text} onChange={(e) => setText(e.target.value)}>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : Array.isArray(conflict.dashboardValue) ? (
                  <textarea value={text} onChange={(e) => setText(e.target.value)} />
                ) : (
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    type={typeof conflict.dashboardValue === 'number' ? 'number' : 'text'}
                    step="any"
                  />
                )}
              </label>
              {error && (
                <p role="alert" className="error">
                  {error}
                </p>
              )}
              <button type="submit" disabled={disabled}>
                Save resolution
              </button>
            </form>
          )}
        </div>
      ))}
      {resolved.length > 0 && (
        <details>
          <summary>Resolved ({resolved.length})</summary>
          <ul>
            {resolved.map((c) => (
              <li key={c.id}>
                {hardwarePathLabel(c.path)} — {c.resolution!.choice} · {c.resolution!.by} ·{' '}
                {formatDateTime(c.resolution!.at)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
function display(value: unknown): string {
  return Array.isArray(value) ? value.map(display).join(', ') : String(value);
}

export function parseConflictEdit(
  conflict: HardwareConflict,
  text: string,
): { value: unknown } | { error: string } {
  if (Array.isArray(conflict.dashboardValue))
    return {
      value: text
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    };
  if (
    typeof conflict.dashboardValue === 'number' ||
    NUMERIC_HARDWARE_FIELDS.has(conflict.path.split('.').at(-1)!)
  ) {
    if (!text.trim()) return { error: 'Enter a number; blank is not zero.' };
    const value = Number(text);
    if (!Number.isFinite(value) || value < 0)
      return { error: 'Enter a finite, non-negative number.' };
    const integer = [
      'quantity',
      'panelsInLongestString',
      'numberOfStrings',
      'arrayModuleIndex',
    ].includes(conflict.path.split('.').at(-1)!);
    if (integer && !Number.isInteger(value)) return { error: 'Enter a whole number.' };
    return { value };
  }
  if (typeof conflict.dashboardValue === 'boolean')
    return text === 'true' || text === 'false'
      ? { value: text === 'true' }
      : { error: 'Choose true or false.' };
  return { value: text.trim() };
}

export function validateConflictValue(
  conflict: HardwareConflict,
  value: unknown,
): string | undefined {
  const candidate: Record<string, unknown> = {};
  const parts = conflict.path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let target = candidate;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    target[key] ??= /^\d+$/.test(parts[i + 1]!) ? [] : {};
    if (Array.isArray(target[key])) {
      const rows = target[key] as unknown[];
      const index = Number(parts[i + 1]);
      while (rows.length <= index) rows.push({});
    }
    target = target[key] as Record<string, unknown>;
  }
  target[parts.at(-1)!] = value;
  const parsed = FieldHardwareSchema.safeParse(candidate);
  return parsed.success
    ? undefined
    : (parsed.error.issues[0]?.message ?? 'Invalid hardware value.');
}
